import { useEffect, useRef } from 'react';
import { Platform, ScrollView, useWindowDimensions, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { trackEvent } from '@/modules/analytics';
import { useRulesEngine } from '@/modules/rulesEngine';
import { useScreenTopInset } from '@/modules/ui';

import { DownloadPanel } from './components/DownloadPanel';
import { HeroVideo } from './components/HeroVideo';

const DESKTOP_BREAKPOINT = 1120;

// The phone frame is 520 tall and bleeds 40px off the bottom (≈480 visible). A
// 540 hero leaves a modest ~60px top gap — used as the stacked height and as the
// desktop floor (desktop stretches to match the left column, never below this).
const HERO_MIN_HEIGHT = 540;

/**
 * TRANSITION TO NATIVE APP — the onboarding terminal (Sparta's
 * TransitionToNativeAppContainer). Prompts the user to get the mobile app
 * (QR + text-link on web, Download CTA on native) alongside a looping app
 * preview. Full-bleed (not the standard Screen footer layout); reads the
 * onboarding header inset so content clears the sticky header.
 */
export function TransitionToNativeAppScreen() {
  const { width } = useWindowDimensions();
  const { topInset } = useScreenTopInset();
  const insets = useSafeAreaInsets();
  const { user } = useRulesEngine();
  const isDesktop = width >= DESKTOP_BREAKPOINT;

  // Terminal-screen view event (Sparta's transition-to-native-app-viewed). The
  // send-link events live with the SMS flow in DownloadPanel. Fires once the
  // user is known.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (user?.uuid && !viewedRef.current) {
      viewedRef.current = true;
      trackEvent('transition-to-native-app-viewed', {
        uuid: user.uuid,
        isNative: Platform.OS !== 'web',
      });
    }
  }, [user?.uuid]);

  const contentMaxWidth = isDesktop ? 1040 : 560;
  const panelMaxWidth = isDesktop ? 480 : 560;

  return (
    <ScrollView
      style={{ flex: 1 }}
      className='bg-background-0'
      keyboardShouldPersistTaps='handled'
      contentContainerStyle={{
        alignItems: 'center',
        alignSelf: 'center',
        flexGrow: 1,
        // Center the content block vertically; flexGrow keeps the container at
        // least viewport-tall, so this centers when there's room and falls back
        // to normal scrolling when the content is taller than the viewport.
        justifyContent: 'center',
        // This terminal screen hides the onboarding header, so `topInset` is 0
        // here — fall back to the raw safe-area top so content clears the iOS
        // status bar / dynamic island. When the header IS shown, `topInset`
        // already includes the inset, so max() avoids double-counting; web has
        // no inset, so this stays 48.
        paddingTop: Math.max(topInset, insets.top) + 48,
        paddingHorizontal: 24,
        paddingBottom: insets.bottom + (isDesktop ? 48 : 40),
        width: '100%',
      }}
    >
      <View
        className='overflow-hidden'
        style={{
          flexDirection: isDesktop ? 'row' : 'column',
          // Desktop (row): stretch columns to equal height so the hero matches the
          // left column. Stacked (column): center the blocks horizontally.
          alignItems: isDesktop ? 'stretch' : 'center',
          gap: isDesktop ? 48 : 40,
          maxWidth: contentMaxWidth,
          width: '100%',
        }}
      >
        <View
          style={{
            flex: isDesktop ? 1 : undefined,
            maxWidth: panelMaxWidth,
            width: '100%',
          }}
        >
          <DownloadPanel />
        </View>
        <View
          style={{
            flex: isDesktop ? 1 : undefined,
            maxWidth: panelMaxWidth,
            width: '100%',
            // Desktop: no fixed height — stretch (above) matches the left column.
            // Stacked: a tight fixed height. Floor guarantees the phone's top gap.
            height: isDesktop ? undefined : HERO_MIN_HEIGHT,
            minHeight: HERO_MIN_HEIGHT,
          }}
        >
          <HeroVideo />
        </View>
      </View>
    </ScrollView>
  );
}
